import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi, writtenTestApi, interviewApi } from '../services/api';
import { useToast } from '../components/Toast';

interface DashboardProps {
  applicant: any;
  onLogout: () => void;
  onUpdate?: () => void;
}

const steps = [
  { key: 'APPLIED', label: '입사지원', shortLabel: '지원', path: '/application' },
  { key: 'AI_REVIEWED', label: 'AI 심사', shortLabel: 'AI', path: null },
  { key: 'WRITTEN_TEST', label: '서면평가', shortLabel: '서면', path: '/written-test' },
  { key: 'INTERVIEW', label: '면접', shortLabel: '면접', path: '/interview' },
  { key: 'OFFER', label: '최종결과', shortLabel: '결과', path: '/result' },
];

const getStepIndex = (status: string) => {
  if (status === 'DRAFT') return 0;
  if (status === 'REJECTED' || status === 'DOC_REJECTED' || status === 'WRITTEN_REJECTED') return -1;
  if (status === 'HIRED') return steps.length;
  const idx = steps.findIndex((s) => s.key === status);
  return idx >= 0 ? idx : 0;
};

const ALL_STATUSES = [
  'DRAFT', 'APPLIED', 'AI_REVIEWED', 'WRITTEN_TEST', 'INTERVIEW', 'OFFER',
  'HIRED', 'DOC_REJECTED', 'WRITTEN_REJECTED', 'REJECTED',
];

const STATUS_LABELS: Record<string, string> = {
  DRAFT: '작성중',
  APPLIED: '지원완료',
  AI_REVIEWED: 'AI 심사완료',
  WRITTEN_TEST: '서면평가',
  INTERVIEW: '면접',
  OFFER: '최종결과',
  HIRED: '입사확정',
  DOC_REJECTED: '서류불합격',
  WRITTEN_REJECTED: '서면불합격',
  REJECTED: '불합격',
};

const DashboardPage: React.FC<DashboardProps> = ({ applicant, onLogout, onUpdate }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const status = applicant?.status || 'DRAFT';
  const isTestAccount = applicant?.email === 'test@grkcon.com';
  const currentStep = getStepIndex(status);
  const isRejected = status === 'REJECTED' || status === 'DOC_REJECTED' || status === 'WRITTEN_REJECTED';

  // 비밀번호 변경
  const [showPwModal, setShowPwModal] = useState(false);
  const [pwCurrent, setPwCurrent] = useState('');
  const [pwNew, setPwNew] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [pwLoading, setPwLoading] = useState(false);
  const [pwError, setPwError] = useState('');

  const handleChangePassword = async () => {
    if (!pwCurrent) { setPwError('현재 비밀번호를 입력해주세요.'); return; }
    if (!pwNew || pwNew.length < 6) { setPwError('새 비밀번호는 6자 이상이어야 합니다.'); return; }
    if (pwNew !== pwConfirm) { setPwError('새 비밀번호가 일치하지 않습니다.'); return; }
    setPwLoading(true);
    setPwError('');
    try {
      await authApi.changePassword(pwCurrent, pwNew, pwConfirm);
      toast.success('비밀번호가 변경되었습니다. 다시 로그인해주세요.');
      localStorage.removeItem('hire_token');
      setTimeout(() => { window.location.href = '/login'; }, 1500);
    } catch (err: any) {
      setPwError(err.response?.data?.message || '비밀번호 변경에 실패했습니다.');
    } finally {
      setPwLoading(false);
    }
  };
  const isDocRejected = status === 'DOC_REJECTED';
  const isWrittenRejected = status === 'WRITTEN_REJECTED';
  const isHired = status === 'HIRED';
  const [allTestsSubmitted, setAllTestsSubmitted] = useState(false);
  const [interviewData, setInterviewData] = useState<any>(null);
  const [selectedStep, setSelectedStep] = useState<number>(currentStep >= 0 ? currentStep : 0);
  const [statusChanging, setStatusChanging] = useState(false);

  const handleTestStatusChange = async (newStatus: string) => {
    if (statusChanging) return;
    setStatusChanging(true);
    try {
      await authApi.updateTestStatus(newStatus);
      if (onUpdate) await onUpdate();
    } catch {
      // ignore
    } finally {
      setStatusChanging(false);
    }
  };

  useEffect(() => {
    if ((status === 'WRITTEN_TEST' || isTestAccount) && applicant?.id) {
      writtenTestApi.getMyTests().then((tests: any[]) => {
        const nonLocked = tests.filter((t: any) => !t.locked);
        const allDone = nonLocked.length > 0 && nonLocked.every(
          (t: any) => t.status === 'SUBMITTED' || t.status === 'EVALUATED'
        );
        setAllTestsSubmitted(allDone);
      }).catch(() => {});
    }
    if ((status === 'INTERVIEW' || isTestAccount) && applicant?.id) {
      interviewApi.getInterviewInfo(applicant.id).then((data: any) => {
        setInterviewData(data.interview || null);
      }).catch(() => {});
    }
  }, [status, applicant?.id, isTestAccount]);

  const renderStageCard = () => {
    // 테스트 계정: 불합격/합격 상태에서도 스텝 카드 표시
    // 불합격
    if (isRejected && !isTestAccount) {
      return (
        <div className="bg-white rounded-xl border border-slate-200 p-4 md:p-6">
          <div className="text-center py-6">
            <div className="w-14 h-14 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-7 h-7 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-base font-semibold text-slate-800 mb-2">
              {isDocRejected ? '서류 심사에서 불합격되었습니다'
                : isWrittenRejected ? '서면 평가에서 불합격되었습니다'
                : '채용 프로세스가 종료되었습니다'}
            </h3>
            <p className="text-sm text-slate-500">
              {isDocRejected
                ? '서류 심사 결과 아쉽게도 불합격되었습니다. 지원해주셔서 감사합니다.'
                : isWrittenRejected
                ? '서면 평가 결과 아쉽게도 불합격되었습니다. 지원해주셔서 감사합니다.'
                : '지원해주셔서 감사합니다. 다음 기회에 다시 만나 뵙기를 바랍니다.'}
            </p>
          </div>
        </div>
      );
    }

    // 합격 (입사 확정)
    if (isHired && !isTestAccount) {
      return (
        <div className="bg-white rounded-xl border border-green-200 p-4 md:p-6">
          <div className="text-center py-6">
            <div className="w-14 h-14 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-base font-semibold text-slate-800 mb-2">축하합니다! 입사가 확정되었습니다</h3>
            <p className="text-sm text-slate-500 mb-4">
              입사 준비 서류를 제출해주세요.
            </p>
            <button
              onClick={() => navigate('/result')}
              className="px-6 py-2.5 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors"
            >
              입사 서류 제출하기
            </button>
          </div>
        </div>
      );
    }

    // selectedStep 기반으로 해당 단계 카드 표시
    const stepKey = steps[selectedStep]?.key;
    // 테스트 계정: 선택한 스텝을 현재 스텝으로 취급
    const effectiveStep = isTestAccount ? selectedStep : currentStep;

    // 입사지원 (DRAFT / APPLIED)
    if (stepKey === 'APPLIED') {
      if (status === 'DRAFT') {
        return (
          <div className="bg-white rounded-xl border border-slate-200 p-4 md:p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-slate-700">입사지원서 작성</h2>
              <span className="text-xs px-2 py-1 bg-amber-50 text-amber-600 rounded-full font-medium">작성중</span>
            </div>
            <div className="space-y-3">
              <CheckItem label="이력서 업로드" done={!!applicant?.resume_url} />
              <CheckItem label="에세이 작성" done={!!applicant?.essay} />
              <button
                onClick={() => navigate('/application')}
                className="w-full mt-4 py-3 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                지원서 작성하기
              </button>
            </div>
          </div>
        );
      }
      // 이미 제출 완료
      return (
        <div className="bg-white rounded-xl border border-slate-200 p-4 md:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-slate-700">지원서 제출 완료</h2>
            <span className="text-xs px-2 py-1 bg-green-50 text-green-600 rounded-full font-medium">완료</span>
          </div>
          <div className="text-center py-4">
            <div className="w-12 h-12 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-sm text-slate-700 font-medium mb-1">지원서가 성공적으로 제출되었습니다</p>
            <p className="text-xs text-slate-500">
              {effectiveStep === 0 ? 'AI 심사가 진행되면 결과를 안내드리겠습니다.' : ''}
            </p>
          </div>
        </div>
      );
    }

    // AI 심사
    if (stepKey === 'AI_REVIEWED') {
      const isDone = effectiveStep > 1;
      const isCurrent = effectiveStep === 1;
      return (
        <div className="bg-white rounded-xl border border-slate-200 p-4 md:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-slate-700">AI 심사</h2>
            <span className={`text-xs px-2 py-1 rounded-full font-medium ${
              isDone ? 'bg-green-50 text-green-600' : isCurrent ? 'bg-indigo-50 text-indigo-600' : 'bg-slate-100 text-slate-400'
            }`}>{isDone ? '통과' : isCurrent ? '진행중' : '대기'}</span>
          </div>
          <div className="text-center py-4">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 ${
              isDone ? 'bg-green-50' : isCurrent ? 'bg-indigo-50' : 'bg-slate-50'
            }`}>
              <svg className={`w-6 h-6 ${isDone ? 'text-green-600' : isCurrent ? 'text-indigo-600' : 'text-slate-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isDone ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                )}
              </svg>
            </div>
            <p className="text-sm text-slate-700 font-medium mb-1">
              {isDone ? 'AI 심사가 완료되었습니다' : isCurrent ? 'AI 심사가 진행 중입니다' : 'AI 심사 대기 중입니다'}
            </p>
            <p className="text-xs text-slate-500">
              {isDone ? '' : isCurrent ? '결과를 기다려주세요.' : '이전 단계를 완료해주세요.'}
            </p>
          </div>
        </div>
      );
    }

    // 서면평가
    if (stepKey === 'WRITTEN_TEST') {
      const isDone = effectiveStep > 2;
      const isCurrent = effectiveStep === 2;
      return (
        <div className="bg-white rounded-xl border border-slate-200 p-4 md:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-slate-700">서면평가</h2>
            <span className={`text-xs px-2 py-1 rounded-full font-medium ${
              isDone ? 'bg-green-50 text-green-600'
                : isCurrent ? (allTestsSubmitted ? 'bg-green-50 text-green-600' : 'bg-indigo-50 text-indigo-600')
                : 'bg-slate-100 text-slate-400'
            }`}>{isDone ? '완료' : isCurrent ? (allTestsSubmitted ? '제출완료' : '진행중') : '대기'}</span>
          </div>
          <div className="text-center py-4">
            {isDone || (isCurrent && allTestsSubmitted) ? (
              <>
                <div className="w-12 h-12 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-3">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-sm text-slate-700 font-medium mb-1">서면 평가가 완료되었습니다</p>
                <p className="text-xs text-slate-500">{isCurrent ? '평가 결과를 기다려주세요.' : ''}</p>
              </>
            ) : isCurrent ? (
              <>
                <div className="w-12 h-12 bg-indigo-50 rounded-full flex items-center justify-center mx-auto mb-3">
                  <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <p className="text-sm text-slate-700 font-medium mb-1">서면평가가 진행 중입니다</p>
                <p className="text-xs text-slate-500 mb-4">아래 버튼을 눌러 시험에 응시해주세요.</p>
                <button
                  onClick={() => navigate('/written-test')}
                  className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  서면평가 응시하기
                </button>
              </>
            ) : (
              <>
                <div className="w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-3">
                  <svg className="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <p className="text-sm text-slate-700 font-medium mb-1">서면평가 대기 중입니다</p>
                <p className="text-xs text-slate-500">이전 단계를 완료해주세요.</p>
              </>
            )}
          </div>
        </div>
      );
    }

    // 면접
    if (stepKey === 'INTERVIEW') {
      const isDone = effectiveStep > 3;
      const isCurrent = effectiveStep === 3;
      const interviewStatus = interviewData?.status;

      if (!isCurrent && !isDone) {
        return (
          <div className="bg-white rounded-xl border border-slate-200 p-4 md:p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-slate-700">면접</h2>
              <span className="text-xs px-2 py-1 bg-slate-100 text-slate-400 rounded-full font-medium">대기</span>
            </div>
            <div className="text-center py-4">
              <div className="w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <p className="text-sm text-slate-700 font-medium mb-1">면접 대기 중입니다</p>
              <p className="text-xs text-slate-500">이전 단계를 완료해주세요.</p>
            </div>
          </div>
        );
      }

      const getInterviewBadge = () => {
        if (isDone) return { label: '완료', color: 'bg-green-50 text-green-600' };
        if (!interviewData) return { label: '대기 중', color: 'bg-amber-50 text-amber-600' };
        if (interviewStatus === 'PROPOSAL_SENT') return { label: '일정 제안됨', color: 'bg-blue-50 text-blue-600' };
        if (interviewStatus === 'APPLICANT_SELECTED') return { label: '확정 대기', color: 'bg-cyan-50 text-cyan-600' };
        if (interviewStatus === 'ALTERNATIVE_PROPOSED') return { label: '검토 중', color: 'bg-amber-50 text-amber-600' };
        if (interviewStatus === 'CONFIRMED') return { label: '확정', color: 'bg-green-50 text-green-600' };
        return { label: '진행중', color: 'bg-indigo-50 text-indigo-600' };
      };
      const badge = getInterviewBadge();

      return (
        <div className="bg-white rounded-xl border border-slate-200 p-4 md:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-slate-700">면접</h2>
            <span className={`text-xs px-2 py-1 rounded-full font-medium ${badge.color}`}>{badge.label}</span>
          </div>
          <div className="text-center py-4">
            {isDone ? (
              <>
                <div className="w-12 h-12 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-3">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-sm text-slate-700 font-medium mb-1">면접이 완료되었습니다</p>
              </>
            ) : !interviewData ? (
              <>
                <div className="w-12 h-12 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-3">
                  <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="text-sm text-slate-700 font-medium mb-1">면접 일정 조율 대기 중</p>
                <p className="text-xs text-slate-500">담당자가 일정을 제안하면 안내드리겠습니다.</p>
              </>
            ) : interviewStatus === 'PROPOSAL_SENT' ? (
              <>
                <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-3">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <p className="text-sm text-slate-700 font-medium mb-1">면접 일정이 제안되었습니다</p>
                <p className="text-xs text-slate-500 mb-3">원하시는 일정을 선택해주세요.</p>
                <button
                  onClick={() => navigate('/interview')}
                  className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  일정 확인하기
                </button>
              </>
            ) : interviewStatus === 'CONFIRMED' ? (
              <>
                <div className="w-12 h-12 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-3">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="text-sm text-slate-700 font-medium mb-1">면접 일정이 확정되었습니다</p>
                {interviewData.scheduled_date && (
                  <p className="text-base font-semibold text-indigo-700 mb-1">
                    {interviewData.scheduled_date} {interviewData.scheduled_time}
                  </p>
                )}
                <button
                  onClick={() => navigate('/interview')}
                  className="mt-2 px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  상세 확인
                </button>
              </>
            ) : (
              <>
                <div className="w-12 h-12 bg-indigo-50 rounded-full flex items-center justify-center mx-auto mb-3">
                  <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </div>
                <p className="text-sm text-slate-700 font-medium mb-1">면접 일정 조율 중입니다</p>
                <button
                  onClick={() => navigate('/interview')}
                  className="mt-2 px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  확인하기
                </button>
              </>
            )}
          </div>
        </div>
      );
    }

    // 최종결과
    if (stepKey === 'OFFER') {
      const isDone = effectiveStep > 4 || isHired;
      const isCurrent = effectiveStep === 4;
      return (
        <div className="bg-white rounded-xl border border-slate-200 p-4 md:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-slate-700">최종 결과</h2>
            <span className={`text-xs px-2 py-1 rounded-full font-medium ${
              isDone || isCurrent ? 'bg-green-50 text-green-600' : 'bg-slate-100 text-slate-400'
            }`}>{isDone ? '확정' : isCurrent ? '합격' : '대기'}</span>
          </div>
          <div className="text-center py-4">
            {isDone || isCurrent ? (
              <>
                <div className="w-12 h-12 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-3">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                  </svg>
                </div>
                <p className="text-sm text-slate-700 font-medium mb-1">최종 합격을 축하드립니다!</p>
                <p className="text-xs text-slate-500">오퍼 레터와 입사 안내를 확인하세요.</p>
                <button
                  onClick={() => navigate('/result')}
                  className="mt-3 px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  결과 확인하기
                </button>
              </>
            ) : (
              <>
                <div className="w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-3">
                  <svg className="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <p className="text-sm text-slate-700 font-medium mb-1">최종 결과 대기 중입니다</p>
                <p className="text-xs text-slate-500">이전 단계를 완료해주세요.</p>
              </>
            )}
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="min-h-screen bg-slate-100">
      {/* 헤더 */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <img
            src="/grk-logo.jpg"
            alt="GRK Career Management"
            className="h-8 md:h-10 object-contain"
            style={{ mixBlendMode: 'multiply' }}
          />
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-600 hidden md:inline">
              {applicant?.name || applicant?.email}님
            </span>
            <button
              onClick={() => { setShowPwModal(true); setPwError(''); setPwCurrent(''); setPwNew(''); setPwConfirm(''); }}
              className="text-sm text-slate-500 hover:text-slate-700 px-3 py-1.5 rounded-lg hover:bg-slate-50 transition-colors"
            >
              비밀번호 변경
            </button>
            <button
              onClick={onLogout}
              className="text-sm text-slate-500 hover:text-slate-700 px-3 py-1.5 rounded-lg hover:bg-slate-50 transition-colors"
            >
              로그아웃
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 md:py-8">
        {/* 환영 메시지 */}
        <div className="mb-6">
          <h1 className="text-lg md:text-xl font-semibold text-slate-800">
            {applicant?.name ? `${applicant.name}님, 환영합니다` : '환영합니다'}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            채용 프로세스 현황을 확인하세요.
          </p>
        </div>

        {/* 테스트 모드 패널 */}
        {isTestAccount && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-amber-600 text-sm font-semibold">TEST MODE</span>
              <span className="text-xs text-amber-500">상태를 자유롭게 변경할 수 있습니다</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {ALL_STATUSES.map((s) => (
                <button
                  key={s}
                  onClick={() => handleTestStatusChange(s)}
                  disabled={statusChanging || status === s}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                    status === s
                      ? 'bg-amber-600 text-white'
                      : 'bg-white border border-amber-300 text-amber-700 hover:bg-amber-100'
                  } disabled:opacity-50`}
                >
                  {STATUS_LABELS[s] || s}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* 프로세스 스텝 */}
        {(!isRejected || isTestAccount) && (
          <div className="bg-white rounded-xl border border-slate-200 p-4 md:p-6 mb-6">
            <h2 className="text-sm font-semibold text-slate-700 mb-4">채용 프로세스</h2>
            <div className="flex items-center justify-between">
              {steps.map((step, idx) => (
                <React.Fragment key={step.key}>
                  <div
                    className="flex flex-col items-center cursor-pointer"
                    onClick={() => setSelectedStep(idx)}
                  >
                    <div
                      className={`w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center text-xs md:text-sm font-medium transition-all hover:scale-110 ${
                        isHired || idx < currentStep
                          ? 'bg-indigo-600 text-white'
                          : idx === currentStep
                          ? 'bg-indigo-100 text-indigo-700 ring-2 ring-indigo-600'
                          : 'bg-slate-100 text-slate-400'
                      } ${selectedStep === idx ? 'ring-2 ring-offset-2 ring-indigo-400' : ''}`}
                    >
                      {isHired || idx < currentStep ? (
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        idx + 1
                      )}
                    </div>
                    <span className={`mt-2 text-xs font-medium ${
                      selectedStep === idx ? 'text-indigo-700 font-semibold' : isHired || idx <= currentStep ? 'text-indigo-600' : 'text-slate-400'
                    }`}>
                      <span className="hidden md:inline">{step.label}</span>
                      <span className="md:hidden">{step.shortLabel}</span>
                    </span>
                  </div>
                  {idx < steps.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-1 md:mx-2 ${
                      isHired || idx < currentStep ? 'bg-indigo-600' : 'bg-slate-200'
                    }`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        {/* 현재 단계 카드 */}
        {renderStageCard()}
      </main>

      {showPwModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">비밀번호 변경</h3>
            <div className="space-y-3" onKeyDown={(e) => { if (e.key === 'Enter' && !pwLoading) handleChangePassword(); }}>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">현재 비밀번호</label>
                <input type="password" value={pwCurrent} onChange={(e) => setPwCurrent(e.target.value)}
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">새 비밀번호</label>
                <input type="password" value={pwNew} onChange={(e) => setPwNew(e.target.value)}
                  placeholder="6자 이상"
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">새 비밀번호 확인</label>
                <input type="password" value={pwConfirm} onChange={(e) => setPwConfirm(e.target.value)}
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              </div>
              {pwError && (
                <div className="p-2.5 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{pwError}</p>
                </div>
              )}
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowPwModal(false)}
                className="flex-1 py-2.5 border border-slate-300 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors">
                취소
              </button>
              <button onClick={handleChangePassword} disabled={pwLoading}
                className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50">
                {pwLoading ? '처리 중...' : '변경'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const CheckItem: React.FC<{ label: string; done: boolean }> = ({ label, done }) => (
  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
    <div className="flex items-center gap-3">
      <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
        done ? 'bg-green-100 text-green-600' : 'bg-slate-200 text-slate-400'
      }`}>
        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
          {done ? (
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          ) : (
            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
          )}
        </svg>
      </div>
      <span className="text-sm text-slate-700">{label}</span>
    </div>
    <span className={`text-xs font-medium ${done ? 'text-green-600' : 'text-slate-400'}`}>
      {done ? '완료' : '미완료'}
    </span>
  </div>
);

export default DashboardPage;
